/*
  Shape tab for a SINGLE analysis: circularity, irregularity and the other
  shape/edge metrics over time, one line per detected object.

  Compare Analyses has had this since the morphology pass shipped; this is the
  same data for the analysis you already have open, so reading one well's shape
  no longer means going to the comparison page and selecting it against itself.

  Metric definitions and the results.json -> chart-row normalisation are shared
  with that page (utils/morphMetrics) so the two cannot disagree.
*/

import React, { useMemo, useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { Line } from 'react-chartjs-2';
import { parseMorphology, availableMetrics } from '../utils/morphMetrics';

const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
                '#FF9F40', '#8BC34A', '#FF5722', '#00BCD4', '#E91E63'];

const ShapeMetricsPanel = ({ parsedJson, frameTimeIso = {}, frameTimestamps = {} }) => {
  const rows = useMemo(() => parseMorphology(parsedJson, frameTimeIso), [parsedJson, frameTimeIso]);
  const metrics = useMemo(() => availableMetrics(rows), [rows]);
  const [metric, setMetric] = useState('circularity');

  const active = metrics.find((m) => m.key === metric) || metrics[0];

  // Elapsed hours when the run carries timestamps, frame index otherwise. Older
  // analyses have no per-frame times, and a frame axis is honest about that.
  const useHours = Object.keys(frameTimestamps || {}).length > 0;
  const xOf = (r) => {
    if (!useHours) return r.frame;
    const s = frameTimestamps[r.frame] ?? frameTimestamps[String(r.frame)];
    return typeof s === 'number' ? +(s / 3600).toFixed(3) : r.frame;
  };

  const chart = useMemo(() => {
    if (!active) return null;
    const byObj = {};
    rows.forEach((r) => { (byObj[r.object] ||= []).push(r); });
    const datasets = Object.entries(byObj)
      .sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10))
      .map(([objId, objRows], i) => {
        objRows.sort((a, b) => a.frame - b.frame);
        return {
          label: `Colony ${objId}`,
          data: objRows.map((r) => ({ x: xOf(r), y: r[active.key] })),
          borderColor: COLORS[i % COLORS.length],
          backgroundColor: COLORS[i % COLORS.length],
          borderWidth: 2,
          pointRadius: 2,
          tension: 0.25,
          // Empty-mask frames are null; leave a gap rather than joining across
          // a period where nothing was measured.
          spanGaps: false,
        };
      });
    return { datasets };
  }, [rows, active, useHours]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" sx={{ color: '#888' }}>
          No shape metrics for this analysis yet. They are written when an
          analysis is precomputed; re-run Segmentation → re-precompute to add
          them to an older one.
        </Typography>
      </Box>
    );
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: false },
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: useHours ? 'Elapsed time (h)' : 'Frame' },
        ticks: { color: '#aaa' }, grid: { color: '#333' },
      },
      y: {
        title: { display: true, text: active?.label || '' },
        ticks: { color: '#aaa' }, grid: { color: '#333' },
      },
    },
    plugins: {
      legend: { labels: { color: '#ddd' } },
      tooltip: { callbacks: { title: (items) =>
        (useHours ? `${items[0].parsed.x} h` : `Frame ${items[0].parsed.x}`) } },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Shape &amp; edge metrics</Typography>
        {active && (
          <Typography variant="caption" sx={{ color: '#888' }}>{active.hint}</Typography>
        )}
      </Box>
      {/* There is no global dark ThemeProvider, so MUI's light-mode defaults
          applied: unselected buttons rendered near-black on a near-black panel
          at 2.2:1 and were effectively invisible. Text colours follow the Color
          Space Explorer group in HSVVisualization (the house style for this
          control) and reach 10.3:1; the border is #666 rather than that group's
          #444, which sits at 2.0:1 -- below the 3:1 WCAG asks of a UI boundary,
          and this control disappearing is the exact bug being fixed.

          Twelve metrics wrap over several rows, so the group allows wrapping
          rather than collapsing the buttons into slivers. */}
      <ToggleButtonGroup
        size="small" exclusive value={active?.key}
        onChange={(e, v) => v && setMetric(v)}
        sx={{
          flexWrap: 'wrap',
          gap: 0.5,
          '& .MuiToggleButton-root': {
            color: '#bbb',
            borderColor: '#666',
            border: '1px solid #666',
            borderRadius: '4px !important',   // group strips radii off inner buttons
            textTransform: 'none',
            px: 1.5, py: 0.3,
            fontSize: '0.8rem',
            '&:hover': { backgroundColor: 'rgba(124,61,237,0.15)', borderColor: '#8a8a8a' },
          },
          '& .Mui-selected': {
            color: '#fff',
            backgroundColor: 'rgba(124,61,237,0.35) !important',
            borderColor: '#7c3aed !important',
          },
        }}
      >
        {metrics.map((m) => (
          <Tooltip key={m.key} title={m.hint}>
            <ToggleButton value={m.key}>
              {m.label}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
      <Box sx={{ flex: 1, minHeight: 260 }}>
        {chart && <Line data={chart} options={options} />}
      </Box>
    </Box>
  );
};

export default ShapeMetricsPanel;
